import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Calendar, Clock, ListChecks, Target, Info, Lightbulb, Play, RotateCcw,
} from 'lucide-react';

/**
 * R2 — Generic assessment introduction gate (all suites except Climara,
 * which keeps its dedicated anonymity gate). Pure presentational — reuses
 * the questionnaire metadata authored in Assessment Studio. No API calls.
 */
export default function AssessmentIntroGate({
  assignment,
  questionnaire,
  companyName,
  questionCount,
  estimatedMinutes,
  answeredCount = 0,
  onStart,
}) {
  const { t, i18n } = useTranslation();
  const isEN = (i18n.language || '').toLowerCase().startsWith('en');
  const hasStarted = answeredCount > 0;

  // TP-UAT (D): business dates anchored at 12:00Z — never timezone-shifted.
  const bizDateLong = (iso, en, opts) =>
    new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(iso)) ? `${iso}T12:00:00Z` : iso)
      .toLocaleDateString(en ? 'en-US' : 'es-ES', { timeZone: 'America/Panama', ...opts });
  const dueDate = assignment?.deadline
    ? bizDateLong(assignment.deadline, isEN, {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null;
  const name = questionnaire?.name || assignment?.title || t('assessmentIntro.fallbackTitle', { defaultValue: 'Assessment' });

  const recommendations = [
    t('assessmentIntro.rec1', { defaultValue: 'Find a quiet place, free of interruptions.' }),
    t('assessmentIntro.rec2', { defaultValue: 'There are no right or wrong answers — respond honestly.' }),
    t('assessmentIntro.rec3', { defaultValue: 'Your progress is saved automatically; you can pause and resume anytime.' }),
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-subtle p-6">
      <Card className="max-w-xl lg:max-w-5xl w-full p-8 lg:p-10 shadow-elegant" data-testid="assessment-intro-gate">
        <div className="lg:grid lg:grid-cols-5 lg:gap-10">
        <div className="lg:col-span-3 min-w-0">
        <div className="flex items-center gap-3 mb-5">
          <span className="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-primary/10">
            <Target className="h-5 w-5 text-primary" />
          </span>
          <div>
            <Badge variant="outline" className="text-xs uppercase tracking-wider">
              {t(`suite.names.${assignment?.suite_code}`, { defaultValue: assignment?.suite_code || '' })}
            </Badge>
            <h1 className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="intro-title">{name}</h1>
          </div>
        </div>

        {questionnaire?.objective && (
          <div className="mb-4" data-testid="intro-objective">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
              {t('assessmentIntro.objective', { defaultValue: 'Objective' })}
            </p>
            <p className="text-sm leading-relaxed text-foreground/85">{questionnaire.objective}</p>
          </div>
        )}
        {assignment?.tp_context && (
          <div className="mb-4 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid="tp-subject-banner-intro">
            <span className="font-semibold text-primary">
              {isEN ? 'Evaluating' : 'Evaluando a'}: {assignment.tp_context.subject_name}
            </span>
            <span className="text-muted-foreground">
              {assignment.tp_context.subject_position ? ` · ${assignment.tp_context.subject_position}` : ''}
              {assignment.tp_context.subject_department ? ` · ${assignment.tp_context.subject_department}` : ''}
              {assignment.tp_context.cycle_name ? ` · ${assignment.tp_context.cycle_name}` : ''}
              {' · '}
              {isEN
                ? (assignment.tp_context.evaluator_role === 'supervisor' ? 'Role: Supervisor' : 'Role: Internal Client')
                : (assignment.tp_context.evaluator_role === 'supervisor' ? 'Rol: Supervisor/a' : 'Rol: Cliente Interno')}
            </span>
          </div>
        )}
        {questionnaire?.description && !questionnaire?.objective && (
          <p className="text-sm leading-relaxed text-foreground/85 mb-4" data-testid="intro-description">
            {isEN ? questionnaire.description : (questionnaire.description_es || questionnaire.description)}
          </p>
        )}

        <div className="grid grid-cols-2 gap-3 mb-5 text-sm">
          {companyName && (
            <div className="flex items-center gap-2.5 text-foreground/85" data-testid="intro-company">
              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" /><span className="truncate">{companyName}</span>
            </div>
          )}
          <div className="flex items-center gap-2.5 text-foreground/85" data-testid="intro-questions">
            <ListChecks className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>{t('assessmentIntro.questionCount', { defaultValue: '{{n}} questions', n: questionCount })}</span>
          </div>
          <div className="flex items-center gap-2.5 text-foreground/85" data-testid="intro-duration">
            <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>{t('assessmentIntro.duration', { defaultValue: 'About {{n}} minutes', n: estimatedMinutes })}</span>
          </div>
          {dueDate && (
            <div className="flex items-center gap-2.5 text-foreground/85" data-testid="intro-deadline">
              <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>{t('assessmentIntro.due', { defaultValue: 'Due by' })}: {dueDate}</span>
            </div>
          )}
        </div>

        {questionnaire?.instructions && (
          <div className="rounded-md border border-border bg-muted/40 p-3.5 mb-4 lg:mb-0" data-testid="intro-instructions">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5" /> {t('assessmentIntro.instructions', { defaultValue: 'Instructions' })}
            </p>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{questionnaire.instructions}</p>
          </div>
        )}
        </div>

        <div className="lg:col-span-2 lg:border-l lg:border-border lg:pl-8 mt-5 lg:mt-0 flex flex-col">

        <div className="rounded-md border border-primary/20 bg-primary/5 p-3.5 mb-6" data-testid="intro-recommendations">
          <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-1.5 flex items-center gap-1.5">
            <Lightbulb className="h-3.5 w-3.5" /> {t('assessmentIntro.recommendations', { defaultValue: 'Recommendations' })}
          </p>
          <ul className="text-sm leading-relaxed space-y-1 list-disc pl-4">
            {recommendations.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>

        {hasStarted && (
          <p className="text-xs text-muted-foreground mb-3" data-testid="intro-resume-note">
            {t('assessmentIntro.resumeNote', { defaultValue: 'You have answered {{a}} of {{n}} questions. You will continue where you left off.', a: answeredCount, n: questionCount })}
          </p>
        )}
        <Button onClick={onStart} className="w-full bg-gradient-primary hover:opacity-90 gap-2" data-testid="intro-start-btn">
          {hasStarted
            ? <><RotateCcw className="h-4 w-4" /> {t('assessmentIntro.resume', { defaultValue: 'Resume assessment' })}</>
            : <><Play className="h-4 w-4" /> {t('assessmentIntro.start', { defaultValue: 'Start assessment' })}</>}
        </Button>
        </div>
        </div>
      </Card>
    </div>
  );
}
